import { formatFileSize } from '../../../lib/api/queries';

/**
 * The shape both the staff and client evidence types collapse to.
 *
 * Deliberately structural rather than one of the two API types: the staff
 * view carries a scan status and an uploader id that a client never sees,
 * and the client view is served from a frozen snapshot. Normalising at the
 * call site means the table below renders identically in the report
 * preview, the PDF's on-screen equivalent, and the client viewer — which is
 * the whole point, since the three disagreeing is how "the PDF doesn't
 * match what I saw" bugs start.
 */
export type EvidenceRow = {
  id: string;
  sectionId: string;
  sectionCode: string;
  criterionCode: string;
  itemText: string;
  referenceCode: string;
  fileName: string;
  sizeBytes: number;
  description: string | null;
  itemComment: string | null;
  uploadedAtUtc: string;
  /** Present on the staff view only; blocks the download link. */
  blocked?: boolean;
  /** Where the file downloads from, or null when it can't be downloaded. */
  href: string | null;
};

/**
 * Evidence supporting one section of the audit, rendered under that
 * section's narrative. Mirrors <c>RenderSectionEvidence</c> in the PDF
 * renderer column for column.
 */
export function SectionEvidenceTable({ rows }: { rows: EvidenceRow[] }) {
  if (rows.length === 0) return null;

  return (
    <section className="mt-6 bg-white border border-gray-200 rounded-lg overflow-hidden">
      <header className="px-4 py-3 border-b border-gray-100 bg-gray-50">
        <h4 className="text-sm font-semibold text-gray-900">Supporting evidence</h4>
        <p className="mt-0.5 text-xs text-gray-500">
          Documents provided during the assessment.
        </p>
      </header>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500
                           border-b border-gray-200">
              <th className="px-4 py-2 font-semibold">Ref</th>
              <th className="px-4 py-2 font-semibold">Evidence item</th>
              <th className="px-4 py-2 font-semibold">Auditor&rsquo;s note</th>
              <th className="px-4 py-2 font-semibold">Document</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map(row => (
              <tr key={row.id} className="align-top">
                <td className="px-4 py-2 whitespace-nowrap font-medium text-gray-700">
                  {row.referenceCode}
                </td>
                <td className="px-4 py-2 text-gray-800">
                  <span className="text-gray-500 mr-1">{row.criterionCode}</span>
                  {row.itemText}
                </td>
                <td className="px-4 py-2 text-gray-600">
                  {firstNonBlank(row.description, row.itemComment) ?? '—'}
                </td>
                <td className="px-4 py-2">
                  <DocumentCell row={row} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * Appendix listing every attachment on the audit, so a reader can find a
 * document by name without knowing which section cited it.
 */
export function EvidenceRegisterTable({ rows }: { rows: EvidenceRow[] }) {
  if (rows.length === 0) {
    return (
      <p className="text-sm text-gray-500 italic">
        No supporting evidence was attached to this audit.
      </p>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500
                           border-b border-gray-200 bg-gray-50">
              <th className="px-4 py-2 font-semibold">Ref</th>
              <th className="px-4 py-2 font-semibold">Section</th>
              <th className="px-4 py-2 font-semibold">Evidence item</th>
              <th className="px-4 py-2 font-semibold">Document</th>
              <th className="px-4 py-2 font-semibold text-right">Size</th>
              <th className="px-4 py-2 font-semibold">Provided</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map(row => (
              <tr key={row.id} className="align-top">
                <td className="px-4 py-2 whitespace-nowrap font-medium text-gray-700">
                  {row.referenceCode}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-gray-600">
                  {row.sectionCode}
                </td>
                <td className="px-4 py-2 text-gray-800">
                  <span className="text-gray-500 mr-1">{row.criterionCode}</span>
                  {row.itemText}
                </td>
                <td className="px-4 py-2"><DocumentCell row={row} /></td>
                <td className="px-4 py-2 whitespace-nowrap text-right text-gray-600">
                  {formatFileSize(row.sizeBytes)}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-gray-600">
                  {formatDate(row.uploadedAtUtc)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DocumentCell({ row }: { row: EvidenceRow }) {
  if (row.blocked) {
    return (
      <span className="text-danger-500">
        {row.fileName} — blocked by malware scan
      </span>
    );
  }
  if (!row.href) {
    return <span className="text-gray-600 break-all">{row.fileName}</span>;
  }
  return (
    <a
      href={row.href}
      className="text-accent-alt-600 hover:text-accent-alt-700 underline
                 underline-offset-2 break-all"
    >
      {row.fileName}
    </a>
  );
}

function firstNonBlank(...candidates: (string | null | undefined)[]): string | null {
  for (const candidate of candidates) {
    if (candidate && candidate.trim().length > 0) return candidate.trim();
  }
  return null;
}

function formatDate(iso: string): string {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime())
    ? '—'
    : parsed.toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
      });
}

/** The report-section key prefix that carries a template section id. */
const SECTION_NARRATIVE_PREFIX = 'section_narrative_';

/**
 * Pull the template section id out of a report section key, or null when
 * the section isn't one of the per-template-section narratives. Mirrors
 * <c>TryGetTemplateSectionId</c> in the PDF renderer.
 *
 * The id comes back in the key's own dashless form. Compare it against an
 * API-supplied section id with {@link normaliseGuid} — the two formats
 * differ, and comparing them raw silently matches nothing.
 */
export function templateSectionIdFromKey(key: string): string | null {
  return key.startsWith(SECTION_NARRATIVE_PREFIX)
    ? key.slice(SECTION_NARRATIVE_PREFIX.length).toLowerCase()
    : null;
}

/** Dashless lower-case form, so the two GUID spellings compare equal. */
export function normaliseGuid(id: string): string {
  return id.replace(/-/g, '').toLowerCase();
}

/**
 * The evidence belonging to one report section, or an empty array when the
 * section isn't a per-template-section narrative.
 */
export function evidenceForSectionKey(
  sectionKey: string, rows: EvidenceRow[],
): EvidenceRow[] {
  const sectionId = templateSectionIdFromKey(sectionKey);
  if (!sectionId) return [];
  return rows.filter(r => normaliseGuid(r.sectionId) === sectionId);
}
